import { useCallback, useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/Button";
import { PrintLocationDialog } from "@/features/timesheetFlow/components/PrintLocationDialog";
import { PrintJobFeedbackError } from "@/lib/printJob";
import {
  printDokumentPreviewViaLocation,
  printDokumentViaLocation,
  printHeroDokumentViaLocation,
  type DokumentPrintVariant,
} from "@/lib/dokumente";

type CommonProps = {
  disabled?: boolean;
  label?: string;
  buttonVariant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  zIndexClass?: string;
  /** Nur Icon/Text-Button ohne Dialog-Trigger-Label-Override. */
  title?: string;
};

export type DokumentPrintActionProps = CommonProps &
  (
    | {
        mode?: "document";
        docId: number;
        variant?: DokumentPrintVariant;
        stufe?: string;
      }
    | {
        mode: "hero";
        heroDocumentId: number;
      }
    | {
        mode: "preview";
        docId: number;
        getPreviewBody: () => { positionen?: unknown; doc?: Record<string, unknown> | null };
      }
  );

export function DokumentPrintAction(props: DokumentPrintActionProps) {
  const {
    disabled,
    label = "Drucken",
    buttonVariant = "secondary",
    size,
    className,
    zIndexClass,
    title,
  } = props;
  const [open, setOpen] = useState(false);

  const handlePrint = useCallback(
    async (locationId: number, copies: number) => {
      try {
        if (props.mode === "hero") {
          await printHeroDokumentViaLocation(props.heroDocumentId, locationId, copies);
          return;
        }
        if (props.mode === "preview") {
          await printDokumentPreviewViaLocation(
            props.docId,
            locationId,
            copies,
            props.getPreviewBody(),
          );
          return;
        }
        await printDokumentViaLocation(props.docId, locationId, copies, {
          variant: props.variant ?? "dokument",
          stufe: props.stufe,
        });
      } catch (err) {
        if (err instanceof PrintJobFeedbackError) {
          throw new Error(err.feedback.message || err.feedback.title || "Druck fehlgeschlagen.");
        }
        throw err;
      }
    },
    [props],
  );

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={size}
        className={className}
        disabled={disabled}
        title={title || label}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <PrintLocationDialog
        open={open}
        onClose={() => setOpen(false)}
        onPrint={handlePrint}
        zIndexClass={zIndexClass}
      />
    </>
  );
}
